import { supabase } from '../../lib/supabase';
import type { LedgerSummary } from '../../shared/lib/money';

import type { EventCategory, EventColor } from './activity-format';

/**
 * Phase 138 RPC surface for the student activity log.
 *
 * The generated `src/types/database.ts` predates these functions, so calls go
 * through the same deliberately-untyped escape hatch `dossier-api.ts` uses. The
 * response shapes below are the contract until the types file is regenerated.
 */
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

/* ------------------------------------------------------------------ types -- */

export interface EventType {
  id: string;
  code: string;
  label: string;
  category: EventCategory;
  color: EventColor;
  icon: string | null;
  amount_direction: 'none' | 'credit' | 'debit';
  requires_amount: boolean;
  requires_follow_up: boolean;
  default_visible_to_student: boolean;
  is_system: boolean;
  sort_order: number;
  archived_at: string | null;
  /** Live events using this type. Non-zero means archive, never delete. */
  usage_count: number;
}

export interface StudentEvent {
  id: string;
  occurred_at: string;
  title: string | null;
  body: string | null;
  amount_minor: number | null;
  currency: string;
  metadata: Record<string, unknown>;
  follow_up_at: string | null;
  follow_up_done_at: string | null;
  visible_to_student: boolean;
  source: 'manual' | 'system' | 'import';
  created_at: string;
  updated_at: string;
  type_id: string;
  type_code: string;
  type_label: string;
  category: EventCategory;
  color: EventColor;
  icon: string | null;
  amount_direction: 'none' | 'credit' | 'debit';
  author_name: string;
  is_mine: boolean;
  assignee_name: string | null;
  /** Server-computed: author or institute admin. The RPC re-checks on delete. */
  can_delete: boolean;
  /** A revision exists, so the timeline shows an "edited" marker. */
  edited: boolean;
}

export interface EventPage {
  items: StudentEvent[];
  has_more: boolean;
}

export interface FollowUpRow {
  id: string;
  student_id: string;
  student_name: string;
  follow_up_at: string;
  title: string | null;
  type_label: string;
  category: EventCategory;
  color: EventColor;
  icon: string | null;
  overdue: boolean;
  assignee_name: string | null;
  is_mine: boolean;
}

/** The `ledger` and `activity` keys phase 138 added to the dossier RPC. */
export interface ActivitySummary {
  total: number;
  last_at: string | null;
  open_follow_ups: number;
}

export type { LedgerSummary };

/* ------------------------------------------------------------- query keys -- */

export const ACTIVITY_KEYS = {
  eventTypes: (instituteId: string) => ['institute', 'event_types', instituteId] as const,
  events: (studentId: string) => ['institute', 'student_events', studentId] as const,
  followUps: (instituteId: string, mine: boolean) =>
    ['institute', 'follow_ups', instituteId, mine] as const,
};

/* -------------------------------------------------------------- catalogue -- */

export async function listEventTypes(
  instituteId: string,
  includeArchived = false,
): Promise<EventType[]> {
  const { data, error } = await rpcUnknown.rpc('institute_list_event_types', {
    p_institute_id: instituteId,
    p_include_archived: includeArchived,
  });
  if (error !== null) throw new Error(error.message);
  return Array.isArray(data) ? (data as EventType[]) : [];
}

export interface UpsertEventTypeInput {
  institute_id: string;
  /** null creates; the server derives a code from the label. */
  id: string | null;
  label: string;
  category: EventCategory;
  color: EventColor;
  icon: string | null;
  amount_direction: 'none' | 'credit' | 'debit';
  requires_amount: boolean;
  requires_follow_up: boolean;
  default_visible_to_student: boolean;
  sort_order: number;
}

export async function upsertEventType(input: UpsertEventTypeInput): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_upsert_event_type', {
    p_institute_id: input.institute_id,
    p_id: input.id,
    p_code: null,
    p_label: input.label,
    p_category: input.category,
    p_color: input.color,
    p_icon: input.icon,
    p_amount_direction: input.amount_direction,
    p_requires_amount: input.requires_amount,
    p_requires_follow_up: input.requires_follow_up,
    p_default_visible_to_student: input.default_visible_to_student,
    p_sort_order: input.sort_order,
  });
  if (error !== null) throw new Error(error.message);
}

export async function setEventTypeArchived(id: string, archived: boolean): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_set_event_type_archived', {
    p_id: id,
    p_archived: archived,
  });
  if (error !== null) throw new Error(error.message);
}

export async function deleteEventType(id: string): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_delete_event_type', { p_id: id });
  if (error !== null) throw new Error(error.message);
}

/* ----------------------------------------------------------------- events -- */

export interface ListEventsInput {
  student_id: string;
  limit?: number;
  /** Keyset cursor: the last row of the previous page. */
  before_occurred_at?: string | null;
  before_id?: string | null;
  categories?: EventCategory[] | null;
  search?: string | null;
  follow_ups_only?: boolean;
}

export async function listStudentEvents(input: ListEventsInput): Promise<EventPage> {
  const { data, error } = await rpcUnknown.rpc('institute_list_student_events', {
    p_student_id: input.student_id,
    p_limit: input.limit ?? 25,
    p_before_occurred_at: input.before_occurred_at ?? null,
    p_before_id: input.before_id ?? null,
    p_categories:
      input.categories !== undefined && input.categories !== null && input.categories.length > 0
        ? input.categories
        : null,
    p_search: input.search ?? null,
    p_follow_ups_only: input.follow_ups_only ?? false,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null || typeof data !== 'object') return { items: [], has_more: false };
  return data as EventPage;
}

export interface AddEventInput {
  student_id: string;
  event_type_id: string;
  occurred_at: string | null;
  title: string | null;
  body: string | null;
  amount_minor: number | null;
  currency: string;
  metadata: Record<string, unknown>;
  follow_up_at: string | null;
  follow_up_assignee_id: string | null;
  visible_to_student: boolean | null;
}

export async function addStudentEvent(input: AddEventInput): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_add_student_event', {
    p_student_id: input.student_id,
    p_event_type_id: input.event_type_id,
    p_occurred_at: input.occurred_at,
    p_title: input.title,
    p_body: input.body,
    p_amount_minor: input.amount_minor,
    p_currency: input.currency,
    p_metadata: input.metadata,
    p_follow_up_at: input.follow_up_at,
    p_follow_up_assignee_id: input.follow_up_assignee_id,
    p_visible_to_student: input.visible_to_student,
  });
  if (error !== null) throw new Error(error.message);
}

export interface UpdateEventInput extends AddEventInput {
  event_id: string;
}

export async function updateStudentEvent(input: UpdateEventInput): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_update_student_event', {
    p_event_id: input.event_id,
    p_event_type_id: input.event_type_id,
    p_occurred_at: input.occurred_at,
    p_title: input.title,
    p_body: input.body,
    p_amount_minor: input.amount_minor,
    p_currency: input.currency,
    p_metadata: input.metadata,
    p_follow_up_at: input.follow_up_at,
    p_follow_up_assignee_id: input.follow_up_assignee_id,
    p_visible_to_student: input.visible_to_student,
  });
  if (error !== null) throw new Error(error.message);
}

export async function deleteStudentEvent(eventId: string): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_delete_student_event', {
    p_event_id: eventId,
  });
  if (error !== null) throw new Error(error.message);
}

export async function completeFollowUp(eventId: string, done: boolean): Promise<void> {
  const { error } = await rpcUnknown.rpc('institute_complete_follow_up', {
    p_event_id: eventId,
    p_done: done,
  });
  if (error !== null) throw new Error(error.message);
}

export async function listFollowUps(instituteId: string, mine: boolean): Promise<FollowUpRow[]> {
  const { data, error } = await rpcUnknown.rpc('institute_list_follow_ups', {
    p_institute_id: instituteId,
    p_mine: mine,
    p_limit: 50,
  });
  if (error !== null) throw new Error(error.message);
  return Array.isArray(data) ? (data as FollowUpRow[]) : [];
}
